import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import PublicIdBadge from "@/components/PublicIdBadge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Heart, MapPinLine } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [doudous, setDoudous] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/connexion"); return; }
    (async () => {
      try {
        const { data } = await api.get("/doudous");
        setDoudous(data);
      } catch (e) {
        toast.error("Impossible de charger vos doudous. Réessayez dans un instant.");
      } finally { setFetching(false); }
    })();
  }, [user, loading, navigate]);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Mon espace famille</span>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-stone-800 mt-2" data-testid="dashboard-title">
              Bonjour {user?.name?.split(" ")[0]} 👋
            </h1>
            <p className="text-stone-600 mt-2">Voici vos doudous enregistrés. Gardez-les en sécurité.</p>
          </div>
          <Link to="/doudou/nouveau" data-testid="add-doudou-button">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-6 font-bold shadow-lg shadow-orange-500/30">
              <Plus size={20} weight="bold" className="mr-2" /> Ajouter un doudou
            </Button>
          </Link>
        </div>

        {fetching ? (
          <p className="text-stone-500">Chargement...</p>
        ) : doudous.length === 0 ? (
          <div className="bg-white rounded-3xl border border-rose-100 p-12 text-center" data-testid="empty-state">
            <Heart size={56} weight="duotone" className="mx-auto text-rose-300" />
            <h3 className="font-display text-xl font-bold text-stone-800 mt-4">Aucun doudou pour l'instant</h3>
            <p className="text-stone-500 mt-2">Créez votre première fiche pour protéger un doudou.</p>
            <Link to="/doudou/nouveau">
              <Button className="mt-6 bg-orange-500 hover:bg-orange-600 rounded-full font-bold">Ajouter mon premier doudou</Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {doudous.map((d, i) => (
              <motion.div
                key={d.doudou_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={`/doudou/${d.doudou_id}`} data-testid={`doudou-card-${d.doudou_id}`}>
                  <div className="bg-white rounded-3xl border border-rose-100 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(249,115,22,0.12)] transition-all">
                    <div className="aspect-square bg-gradient-to-br from-rose-100 to-orange-100 grid place-items-center overflow-hidden">
                      {d.photo ? (
                        <img src={d.photo} alt={d.name} className="w-full h-full object-contain" />
                      ) : (
                        <Heart size={80} weight="duotone" className="text-rose-300" />
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-display text-xl font-bold text-stone-800 truncate">{d.name}</h3>
                        {d.status === "lost" ? (
                          <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-bold">Perdu</span>
                        ) : (
                          <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">À la maison</span>
                        )}
                      </div>
                      {d.public_id && (
                        <div className="mt-2">
                          <PublicIdBadge publicId={d.public_id} size="sm" testId={`doudou-card-public-id-${d.doudou_id}`} />
                        </div>
                      )}
                      <p className="text-stone-500 text-sm mt-1 line-clamp-2">{d.description || "Pas de description"}</p>
                      {d.department && (
                        <p className="text-stone-400 text-xs mt-2 flex items-center gap-1"><MapPinLine size={12} /> Dép. {d.department}</p>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
